import { loading } from "src/styled";
import { devices, mapper, sizeMapper } from "src/styles";
import styled, { css } from "styled-components";
import FormLabel from "./FormLabel";

export interface FormGroupProps {
  disabled?: boolean;
  flex?: boolean;
  grid?: boolean;
  gridColumnGap?: string;
  gridColumns?: string;
  gridRowGap?: string;
  hasValue?: number | string | boolean;
  inline?: boolean;
  isHidden?: boolean;
  isLoading?: boolean;
  margin?: string;
  padding?: string;
  raisedLabel?: boolean;
  selfFlexStart?: boolean;
  size?:
    | "xxSmall"
    | "xSmall"
    | "small"
    | "medium"
    | "large"
    | "xLarge"
    | "xxLarge"
    | "xxxLarge";
  width?: string;
}

const FormGroup = styled.div<FormGroupProps>`
  margin: ${(props) => sizeMapper(props.margin, "no no sMedium")};
  padding: ${(props) => sizeMapper(props.padding, "no")};
  position: relative;
  text-transform: none;

  ${(props) =>
    props.inline &&
    css`
      display: inline-block;
    `}

  ${(props) =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}

  ${(props) =>
    props.width &&
    css`
      width: ${props.width};
    `};

  ${(props) =>
    props.grid &&
    css`
      width: 100%;
      display: grid;
    `};

  ${(props) =>
    props.gridColumns &&
    css`
      grid-template-columns: ${props.gridColumns};
    `};

  ${(props) =>
    props.gridColumnGap &&
    css`
      grid-column-gap: ${props.gridColumnGap};
    `};

  ${(props) =>
    props.gridRowGap &&
    css`
      grid-row-gap: ${props.gridRowGap};
    `};

  ${(props) =>
    props.isLoading &&
    css`
      &:after {
        content: "";
        position: absolute;
        top: 2px;
        right: 0;
        ${loading("20px")};
      }
    `};

  ${(props) =>
    (props.hasValue === 0 || props.hasValue || props.raisedLabel) &&
    css`
      ${FormLabel} {
        font-size: 0.8rem;
        transform: none;
      }
    `};

  ${(props) =>
    props.selfFlexStart &&
    css`
      align-self: flex-start;
    `};

  ${(props) =>
    props.isHidden &&
    css`
      display: none;
    `}

  ${(props) =>
    props.flex &&
    css`
      display: flex;
      align-items: center;
    `}

  ${FormLabel} {
    transition: transform 0.2s, font-size 0.2s;
    font-size: ${(props) =>
      mapper(
        props.size,
        {
          xxxSmall: "0.4rem", // 6px
          xxSmall: "0.5rem", // 8px
          xSmall: "0.6rem", // 9px
          small: "0.8rem", // 12px
          medium: "0.9rem", // 14px
          large: "1rem", // 16px
          xLarge: "1.2rem", // 18px
          xxLarge: "1.5rem", // 24px
          xxxLarge: "1.7rem", // 27px
          extraLarge: "2.5rem", // 40px
        },
        "medium"
      )};

    ${devices.tablet} {
      font-size: ${(props) =>
        mapper(
          props.size,
          {
            xxxSmall: "0.4rem", // 6px
            xxSmall: "0.5rem", // 8px
            xSmall: "0.7rem", // 11px
            small: "0.8rem", // 12px
            medium: "0.9rem", // 14px
            large: "1rem", // 16px
            xLarge: "1.2rem", // 19px
            xxLarge: "1.4rem", // 22px
            xxxLarge: "1.5rem", // 24px
            extraLarge: "3.5rem", // 56px
          },
          "medium"
        )};
    }

    ${devices.desktop} {
      font-size: ${(props) =>
        mapper(
          props.size,
          {
            xxxSmall: "0.5rem", // 8px
            xxSmall: "0.688rem", // 11px
            xSmall: "0.75rem", // 12px
            small: "0.875rem", // 14px
            medium: "1rem", // 16px
            large: "1.5rem", // 24px
            xLarge: "2rem", // 32px
            xxLarge: "2.25rem", // 36px
            xxxLarge: "4.5", // 72px
          },
          "medium"
        )};
    }

    ${(props) =>
      props.flex &&
      css`
        margin-right: 5px;
      `}
  }
`;

export default FormGroup;
